import { useRef, useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function ImageCarousel({ images }: { images: { src: string; alt: string }[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const updateActive = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setActive(Math.max(0, Math.min(idx, images.length - 1)));
  }, [images.length]);

  const scrollTo = useCallback((idx: number) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
  }, []);

  const next = useCallback(() => scrollTo(Math.min(active + 1, images.length - 1)), [active, images.length, scrollTo]);
  const prev = useCallback(() => scrollTo(Math.max(active - 1, 0)), [active, scrollTo]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => window.requestAnimationFrame(updateActive);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [updateActive]);

  if (images.length === 0) return null;

  return (
    <div className="group relative w-full overflow-hidden rounded-sm bg-muted">
      <div
        ref={scroller}
        className="flex aspect-[4/3] w-full snap-x snap-mandatory overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((img, i) => (
          <figure key={img.src} className="relative h-full w-full flex-none snap-start">
            <img
              src={img.src}
              alt={img.alt}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              className="h-full w-full object-cover"
            />
          </figure>
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Foto anterior"
            onClick={prev}
            disabled={active === 0}
            className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-foreground opacity-0 transition-all hover:bg-black/80 hover:scale-105 disabled:opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Próxima foto"
            onClick={next}
            disabled={active === images.length - 1}
            className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-foreground opacity-0 transition-all hover:bg-black/80 hover:scale-105 disabled:opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="pointer-events-none absolute bottom-1 left-1/2 flex -translate-x-1/2 gap-1">
            {images.map((img, i) => (
              <button
                key={img.src}
                type="button"
                aria-label={`Ir para foto ${i + 1}`}
                aria-current={i === active ? "true" : undefined}
                onClick={() => scrollTo(i)}
                className="pointer-events-auto flex h-9 w-6 items-center justify-center"
              >
                <span
                  aria-hidden="true"
                  className={`block h-1.5 rounded-full transition-all ${
                    i === active ? "w-6 bg-nook-green" : "w-1.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
